import { FC } from 'react';
import { Radio } from 'antd';
import { Controller, useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { IFormControlProps, IRadioOption } from './types';

function serializeRadioValue(value: IRadioOption['value']) {
  if (value === null) {
    return '';
  }

  return String(value);
}

function matchRadioOptionValue(
  incoming: string | number | boolean | null | undefined,
  radioOptions: IRadioOption[],
): IRadioOption | undefined {
  return radioOptions.find(option => {
    if (option.value === null) {
      return incoming === '' || incoming === null || incoming === undefined || incoming === 'null';
    }

    return String(option.value) === String(incoming);
  });
}

export const FormControl: FC<IFormControlProps> = ({
  radioOptions,
  name,
  radioGroupProps,
  rules,
  onChangeEffect,
  isWide = false,
  className,
  noPadding,
  columnsAmount,
  disabled,
}) => {
  const intl = useIntl();
  const { control } = useFormContext();
  const columns = columnsAmount ?? radioOptions.length;

  return (
    <div className={className} style={{ width: isWide ? '100%' : '60%' }}>
      <Controller
        rules={rules}
        control={control}
        name={name}
        render={({ field, fieldState: { error } }) => {
          const hasNullOption = radioOptions.some(option => option.value === null);
          const normalizedGroupValue =
            hasNullOption && (field.value === '' || field.value === undefined) ? null : field.value;

          return (
            <>
              <Radio.Group
                {...radioGroupProps}
                optionType={'button'}
                buttonStyle={'solid'}
                name={field.name}
                disabled={disabled}
                value={serializeRadioValue(normalizedGroupValue)}
                onChange={event => {
                  const selected = matchRadioOptionValue(event.target.value, radioOptions);
                  field.onChange(selected ? selected.value : event.target.value);
                  onChangeEffect?.(event);
                }}
                className={'grid w-full gap-2 md:gap-3'}
                style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
              >
                {radioOptions.map(({ value, labelKey }) => {
                  const label = intl.formatMessage({ id: labelKey });
                  const isActive =
                    value === null
                      ? field.value === null || field.value === '' || field.value === undefined
                      : String(field.value) === String(value);

                  return (
                    <Radio.Button
                      key={`${name}-${String(value)}`}
                      value={serializeRadioValue(value)}
                      disabled={disabled}
                      data-testid={`radio-${name}-${value}`}
                      className={[
                        'm-0! me-0! inline-flex h-auto! min-w-4 items-center justify-center overflow-hidden',
                        'rounded-lg! border! border-s! border-solid! text-center font-medium md:min-w-20',
                        noPadding ? 'p-0!' : 'px-4! py-2!',
                        error
                          ? 'border-red-400!'
                          : isActive
                          ? 'border-brand! bg-brand! text-white! hover:border-brand! hover:bg-brand! hover:text-white!'
                          : 'border-dust! bg-white! text-ash! hover:border-ash! hover:text-ash!',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {label}
                    </Radio.Button>
                  );
                })}
              </Radio.Group>
              {error?.message ? <p className={'mb-0 mt-1 text-sm text-red-500'}>{error.message}</p> : null}
            </>
          );
        }}
      />
    </div>
  );
};
